<template>
  <div class="container pt-5">

    <div class="level">
      <div class="level-left buttons flex-row mb-0">
        <div class="title is-4 mr-3 ml-1 my-2">
          {{ product }} Project Proposals
        </div>
      </div>

      <div class="level-right buttons">
        <v-fade-transition>
          <button class="button mb-0"
            v-if="proposalsSelected.length > 0"
            @click="launchConfirmDeleteBulk()">
            <span class="icon is-medium">
              <i class="fas fa-trash has-text-danger"></i>
            </span>
            <span>
              Delete {{ proposalsSelected.length }} Selected Proposals
            </span>
          </button>
        </v-fade-transition>

        <v-search
          class="mx-2"
          v-model="searchStr"
          @search="searchProjects"
          placeholder="Search for projects"/>

        <button
          title="Sync all projects with CRM"
          v-if="user.permission == 'admin'"
          @click="syncProjects()" 
          :disabled="syncing == true"
          :class="{ 'is-loading': syncing == true }"
          class="button is-link mb-0">
          <span class="icon">
            <i class="fas fa-sync"></i>
          </span>
          <span>Sync with CRM</span>
        </button>

        <button
          title="Add a Proposal"
          @click="addingProposal = true; showSearch = true" 
          class="button is-primary mb-0">
          <span class="icon">
            <i class="fas fa-plus"></i>
          </span>
          <span>Add a Proposal</span>
        </button>
      </div>
    </div>

    <v-fade-transition mode="out-in">
      <table :key="`${product}-${pagination.page}`" class="table is-hoverable is-fullwidth">
        <thead v-if="proposalIds.length > 0">
          <tr>
            <th>
              <label class="checkbox" v-if="proposalIds.length > 0">
                <input
                  type="checkbox"
                  v-model="allSelected">
              </label>
            </th>
            <th v-for="(col, i) in headers"
              :key="i">
              {{ col.label }}
            </th>
          </tr>
        </thead>

        <tbody>
          <template v-for="project in projects">

            <project-details-row
              :key="project.op_no"
              :colspan="headers.length + 2"
              :project="project"
              @add-proposal="launchAddProposal"
            />

            <template v-for="proposal in project.proposals">
              <tr
                class="pointer v-centered"
                v-if="proposal.proposal_object"
                @click="goToProposal(project, proposal.id)"
                :key="proposal.id">
                <td @click.stop="">
                  <label class="checkbox">
                    <input
                      type="checkbox"
                      :checked="proposalsSelected.indexOf(proposal.id) != -1"
                      @input="selectProposal(proposal.id)">
                  </label>
                </td>
                <template v-for="(col, i) in headers">
                  <td :key="i" v-if="col.component != 'VDatepicker'">
                    <span v-if="col.format != 'date'">
                      {{ proposal.proposal_object[col.field] }}
                    </span>
                    <span v-if="col.format == 'date'" class="is-size-7">
                      <span v-if="proposal.proposal_object[col.field]">
                        {{ formatDate(parseDate(proposal.proposal_object[col.field])) }}
                      </span>
                    </span>
                    <span 
                      class="tag is-primary is-light ml-2" 
                      v-if="col.field === 'name' && proposal.proposal_object.proposal_type === CONDENSED"
                    >
                      Condensed Proposal
                    </span>
                  </td>

                  <td :key="i" v-else @click.stop="" >
                    <div class="flex-row field has-addons">
                      <v-datepicker
                        @click.stop=""
                        v-if="col.component == 'VDatepicker'"
                        size="is-small"
                        :value="parseDate(proposal.proposal_object[col.field])"
                        @input="
                          proposal.proposal_object[col.field] = $event;
                          saveChanges(project.id, proposal.id, proposal.proposal_object)
                        "
                      />
                      <button
                        class="button is-small"
                        @click.stop="
                          proposal.proposal_object[col.field] = null;
                          /* saveChanges(project.id, proposal.id, proposal.proposal_object) */
                        ">
                        <span class="icon is-medium">
                          <i class="fas fa-times"></i>
                        </span>
                      </button>
                    </div>
                  </td>
                </template>

                <!-- record actions -->
                <td>
                  <p class="buttons buttons-right">
                    <button
                      class="button is-small"
                      @click.stop="copyProposal(proposal, project.id)">
                      <span class="icon is-medium">
                        <i class="fas fa-copy"></i>
                      </span>
                      <span>
                        Duplicate
                      </span>
                    </button>
                    <button
                      class="button is-small"
                      @click.stop="launchConfirmDelete(
                        proposal.id,
                        proposal.proposal_object.name,
                        project.id,
                        project.project_name
                        )">
                      <span class="icon is-medium">
                        <i class="fas fa-trash"></i>
                      </span>
                      <span>
                        Delete
                      </span>
                    </button>
                  </p>
                </td>
              </tr>
            </template>

          </template>
        </tbody>
      </table>
    </v-fade-transition>

    <!-- pagination -->
    <v-pagination
      v-model="pagination.page"
      :length="pagination.pages">
    </v-pagination>

    <div
      class="modal"
      :class="{ 'is-active': addingProposal }">
      <div class="modal-background"
        @click="addingProposal = false; showSearch = false">
      </div>
      <div class="modal-content">
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Add New Proposal</p>
            <button
              class="delete"
              aria-label="close"
              @click="addingProposal = false; showSearch = false">
            </button>
          </header>
          <section class="modal-card-body">
            <div class="field">
              <label class="label">Project</label>
              <search-autocomplete
                v-if="showSearch" 
                v-model="proposal.project_id"
                :project.sync="projectSelected" 
              />
              <v-select
                v-else
                class="is-fullwidth"
                placeholder="Enter proposal name"
                item-text="project_name"
                item-value="id"
                :options="projects"
                v-model="proposal.project_id"
              />
              <span class="help">
                Please choose a project for which this proposal will be created
              </span>
            </div>
            <div class="field">
              <label class="label">Proposal Name*</label>
              <v-text-field
                required
                placeholder="Enter proposal name"
                v-model="proposal.proposal_object.name"
              />
            </div>
            <div class="field">
              <label class="label">Proposal Type*</label>
              <v-radio-button
                required
                v-model="proposalType"
                :options="proposalTypes"
              >
              </v-radio-button>
            </div>
            <div class="field" v-if="duplicating.status">
              <label class="checkbox">
                <input type="checkbox" v-model="duplicating.keep.pricing">
                Keep pricing information
              </label>
            </div>
          </section>
          <footer class="modal-card-foot">
            <button class="button is-primary" @click="addProposal()">
              Save
            </button>
            <button class="button is-primary" @click="addProposal(true)">
              Save and Edit
            </button>
            <button class="button" @click="addingProposal = false; showSearch = false">
              Cancel
            </button>
          </footer>
        </div>
      </div>
    </div>

    <div
      class="modal"
      :class="{ 'is-active': confirmDelete }">
      <div class="modal-background"
        @click="confirmDelete = false">
      </div>
      <div class="modal-content">
        <article class="message is-danger">
          <div class="message-header">
            <p>Delete Confirmation</p>
            <button
              class="delete"
              aria-label="delete"
              @click="confirmDelete = false">
            </button>
          </div>
          <div class="message-body is-danger has-text-centered">
            <p class="pb-3" v-if="bulkDeleting">
              Are you sure you want to delete the {{ proposalsSelected.length }}
              selected proposals?
            </p>
            <p class="pb-3" v-else>
              Are you sure you want to delete proposal
              {{ toDelete.name }} for project {{ toDelete.project_name }}?
            </p>
            <div class="buttons is-hcentered">
              <button
                class="button is-danger"
                @click="bulkDeleting ? bulkDelete() :
                  deleteProposal(toDelete.project_id, toDelete.id)">
                Yes
              </button>
              <button class="button" @click="confirmDelete = false; clearToDelete()">
                Cancel
              </button>
            </div>
          </div>
        </article>
      </div>
    </div>

    <v-loading
      :key="`${product}-proposals`" 
      :value="loading" 
      :message="`Loading ${product} proposals ...`"/>

  </div>
</template>

<script>
import VFadeTransition from '@/components/transitions/VFadeTransition'
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import VPagination from '@/components/base/VPagination'
import VDatepicker from '@/components/base/VDatepicker'
import VSearch from '@/components/base/VSearch'
import VLoading from '@/components/base/VLoading'
import VRadioButton from '@/components/base/VRadioButton'
import ProjectDetailsRow from '@/components/proposalmaker/ProjectDetailsRow'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'

import { mitrexProposal } from '@/data/mitrex-proposal.js'
import { cladifyProposal } from '@/data/cladify-proposal.js'
import { condensedProposal } from '@/data/condensed-proposal.js'
import { parseJSON, format } from 'date-fns'
import { CONDENSED, FULL } from '@/helpers/constants'

import { mapState, mapMutations } from 'vuex'

export default {
  components: {
    VFadeTransition, VTextField, VSelect, VPagination, VRadioButton,
    VDatepicker, VSearch, VLoading, ProjectDetailsRow, SearchAutocomplete
  },
  data() {
    return {
      CONDENSED: CONDENSED,
      proposalTypes: [
        { label: 'Condensed', value: CONDENSED },
        { label: 'Full', value: FULL }
      ],
      proposalType: CONDENSED,
      searchStr: '',
      parseDate: (date) => { return date ? parseJSON(date) : date },
      formatDate: (date) => format(date, "yyyy-MM-dd HH:mm:ss"),
      allSelected: false,
      proposalsSelected: [],
      headers: [
          { label: 'Proposal Name', field: 'name', format: '' },
          { label: 'Created On', field: 'created_on', format: 'date' },
          { label: 'Created By', field: 'created_by', format: '' },
          { label: 'Sent On', field: 'sent_on', component: 'VDatepicker' }
      ],
      projects: [],
      projectSelected: null,
      pagination: { page: 1 },
      addingProposal: false,
      duplicating: {
        status: false,
        keep: {
          pricing: true
        }
      },
      proposal: {
        project_id: null,
        proposal_object: {}
      },
      showSearch: false,
      confirmDelete: false,
      bulkDeletion: false,
      toDelete: {
        id: null,
        name: null,
        project_id: null,
        project_name: null
      },
      loading: false,
      syncing: false
    }
  },
  computed: {
    ...mapState("auth", ["user"]),
    proposalIds() {
      return [].concat.apply([],
        this.projects.map(project => {
        return project.proposals;
      })).map(d => d.id);
    },
    proposalProject() {
      if (this.proposal.project_id) {
        return this.projects.filter(d => {
          d.id = this.proposal.project_id;
        })
      }
      return null
    },
    bulkDeleting() {
      if (this.bulkDeletion == true && this.proposalsSelected.length > 0) {
        return true
      } else {
        return false
      }
    },
    product() {
      return this.$route.params.product;
    }
  },
  watch: {
    allSelected(state) {
      var proposalsSelected = this.proposalsSelected;
      if (state) {
        this.proposalIds.forEach(id => {
          const index = proposalsSelected.indexOf(id);
          if (index == -1) {
            proposalsSelected.push(id);
          }
        })
      } else {
        proposalsSelected.splice(0, proposalsSelected.length);
      }
    },
    'pagination.page'(newPage, oldPage) {
      if (newPage != oldPage) {
        if (!this.searchStr) {
          this.getProjects();
        } else {
          this.searchProjects(this.searchStr);
        }
      }
    },
    searchStr(v) {
      if (!v) {
        this.getProjects();
      }
    },
    product() {
      this.loading = true;
      this.pagination.page = 1;
      this.getProjects();
      this.assignDefaultProposal();
    },
    proposalType() {
      this.assignDefaultProposal();
    }
  },
  created() {
    this.loading = true;
    this.getProjects();
    this.assignDefaultProposal();
  },
  methods: {
    ...mapMutations("app", ["setNotification"]),
    assignDefaultProposal() {
      const proposalName = this.proposal?.proposal_object?.name
      if (this.proposalType === CONDENSED) {
        this.proposal.proposal_object = Object.assign(
          {}, 
          {...condensedProposal, name: proposalName}
        );
      } else if (this.product == 'Mitrex') {
        this.proposal.proposal_object = Object.assign(
          {}, 
          {...mitrexProposal, name: proposalName}
        );
      } else if (this.product == 'Cladify') {
        this.proposal.proposal_object = Object.assign(
          {}, 
          {...cladifyProposal, name: proposalName}
        );
      }
    },
    getProjects() {
      let params = Object.assign(this.pagination);
      params.product_brand = this.product;
      params.has_proposals = true;

      this.$http.projects.get(params)
        .then(resp => {
          this.projects = resp.data.results;
          this.pagination = resp.data.pagination;
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        })
    },
    searchProjects(v) {
      if(v) {
        this.$http.projects.search({
          q: v, page: this.pagination.page, product_brand: this.product
        }).then(resp => {
          this.replaceProjects(resp.data);
        }).catch(err => console.log(err));
      }
    },
    syncProjects() {
      this.syncing = true;
      this.$http.projectSync.sync().then(() => {
        this.getProjects();
        this.syncing = false;
      }).catch(() => {
        this.syncing = false;
      })
    },
    replaceProjects(data) {
      this.projects = data.results;
      this.pagination = data.pagination;
    },
    launchAddProposal(projectId) {
      this.proposal.project_id = projectId;
      this.addingProposal = true;
    },
    addProposal(edit=false) {
      const { proposal, projectSelected, proposalType, product } = this;

      // Add in project info;
      const project = projectSelected
        ? projectSelected
        : this.projects.filter(d => d.id == proposal.project_id)[0];
      proposal.proposal_object.project_info.name.value = project.project_name;
      proposal.proposal_object.project_info.address.value = project.crm_object.Address;
      proposal.proposal_object.project_info.sales_person.value = {
        'From': project.crm_object.Owner.name,
        'Phone': project.sales_person_primary_phone,
        'Mobile': project.sales_person_secondary_phone,
        'Email': project.crm_object.Owner.email,
        'Estimator': 'Neda Emam'
      }
      proposal.proposal_object.project_info.contact.bind.options = [];
      if (project.crm_object.Contact_Roles) {
        project.crm_object.Contact_Roles.forEach(contact => {
          proposal.proposal_object.project_info.contact.bind.options.push({
            'To': contact.Account_Name ? contact.Account_Name.name : null,
            'Name': contact.Full_Name,
            'Phone': contact.Phone,
            'Email': contact.Email,
            'Quote No.': ''
          })
        })
        if (proposal.proposal_object.project_info.contact.bind.options.length > 0) {
          proposal.proposal_object.project_info.contact.value = proposal.proposal_object.project_info.contact.bind.options[0]
        }
      }
      // Add in other info
      proposal.proposal_object.created_on = new Date();
      proposal.proposal_object.created_by = this.user.display_name;
      proposal.proposal_object.sent_on = null;
      proposal.proposal_object.project_crm_id = project.crm_object.id;

      if (this.duplicating.status) {
        if (!this.duplicating.keep.pricing) {
          proposal.proposal_object.proposal_content.forEach(section => {
            if (section.name == "Pricing Summary" || section.name == "Detailed Pricing") {
              section.subsections = [];
            }
          })
        }
      }

      if (proposalType === CONDENSED) {
        proposal.proposal_object.proposal_info.forEach(section => {
          if (section.id === 'quote_type') {
            section.bind = {
              ...section.bind, 
              options: [...section.bind.options.filter(o => o.product_brand === product)]
            }
          }
        })
      }

      // Send to server
      this.$http.proposals.post(proposal.project_id, {
        proposal: proposal.proposal_object
      }).then(resp => {
        this.showSearch = false;
        this.addProposalSuccess(resp, edit);
      }).catch(err => {
        this.addingProposal = false;
        this.setNotification({
          type: 'success',
          status: true,
          message: err.message
        });
      });
    },
    goToProposal(project, proposalId) {
      this.$router.push({
        name: 'ProposalMaker',
        params: {
          projectId: project.id,
          proposalId: proposalId,
          project: project
        }
      });
    },
    addProposalSuccess(resp, edit) {
      this.addingProposal = false;
      this.duplicating.status = false;
      this.duplicating.keep.pricing = true;

      this.projects.forEach(project => {
        if (project.id == this.proposal.project_id) {

          project.proposals.push({
            id: resp.data.id,
            proposal_object: resp.data.proposal_object,
            version: resp.data.version
          })

          if (edit) {
            this.goToProposal(project, resp.data.id);
          }
        }
      })

      this.setNotification({
        type: 'success',
        status: true,
        message: 'Proposal successfully added'
      });
    },
    copyProposal(proposal, projectId) {
      this.proposal.proposal_object = Object.assign({}, proposal.proposal_object);
      this.duplicating.status = true;
      this.launchAddProposal(projectId);
    },
    launchConfirmDelete(proposalId, proposalName, projectId, projectName) {
      this.confirmDelete = true;
      this.toDelete = {
        id: proposalId,
        name: proposalName,
        project_id: projectId,
        project_name: projectName
      }
    },
    launchConfirmDeleteBulk() {
      this.confirmDelete = true;
      this.bulkDeletion = true;
    },
    clearToDelete() {
      Object.keys(this.toDelete).forEach(key => {
        this.toDelete[key] = null;
      })
    },
    bulkDelete() {
      const selectedCopy = [...this.proposalsSelected];
      selectedCopy.forEach(proposalId => {
        this.projects.forEach(p => {
          let projectProposalIds = p.proposals.map(d => d.id);
          if (projectProposalIds.indexOf(proposalId) != -1) {
            this.deleteProposal(p.id, proposalId);
            this.selectProposal(proposalId);
          }
        })
      })
    },
    deleteProposal(projectId, proposalId) {
      this.$http.proposal.delete(
        projectId,
        proposalId
      ).then(() => {
        this.deleteSuccess(projectId, proposalId);
      }).catch(err => {
        this.deleteError(err);
      })
    },
    deleteSuccess(projectId, proposalId) {
      this.confirmDelete = false;

      var vm = this;
      this.projects.forEach(project => {
        if (project.id == projectId) {
          project.proposals.forEach((proposal, i) => {
            if (proposal.id == proposalId) {
              project.proposals.splice(i, 1);
              vm.clearToDelete();
              const selectedIndex = vm.proposalsSelected.indexOf(proposalId);
              if (selectedIndex != -1) {
                vm.proposalsSelected.splice(selectedIndex, 1)
              }
            }
          })
        }
      });

      this.bulkDeletion = false;
      this.setNotification({
        type: 'success',
        status: true,
        message: 'Proposal successfully deleted'
      });
    },
    deleteError(err) {
      this.clearToDelete();

      this.bulkDeletion = false;
      this.confirmDelete = false;
      this.setNotification({
        type: 'danger',
        status: true,
        message: err.message
      });
    },
    selectProposal(id) {
      const index = this.proposalsSelected.indexOf(id);
      if (index == -1) {
        this.proposalsSelected.push(id);
      } else {
        this.proposalsSelected.splice(index, 1);
      }
    },
    saveChanges(projectId, proposalId, proposal) {
      this.$http.proposal.put(
        projectId,
        proposalId,
        { proposal: proposal }
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.level.level--no-margin {
  margin-bottom: 0em;
}

.buttons.is-hcentered {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
}

.flex-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.pointer {
  cursor: pointer;
}
</style>
